import { el, clear } from "../util/dom";
import type { Bucket } from "./window";
import { center, toPlot, plotFloor, formatPlotValue } from "../displayMode";

const BAR_SCALE_PX = 135; // half-height of the chart's plot area, matches legacy's visual proportions

/** Mirrors legacy's `avgMode` global (see legacy/dashboard.html lines
 * 1447, 2103-2108, 2336-2358): "single" draws one marker at the bucket's
 * overall avg (the existing, already-correct behavior); "double" draws up
 * to two markers -- one at `bullishAvg`, one at `bearishAvg` -- each only
 * when that side actually has data. Defaults to "single", same as
 * legacy's default.
 *
 * Unlike legacy (which always read raw per-tick data), this rebuild's data
 * can come from resolution-selected rollup spans for wider windows (see
 * btclock-api's /v1/history `_auto_select_resolution`), which carry no
 * per-tick bullish/bearish split. For a bucket with real data but neither
 * split available, "double" falls back to the single overall-avg marker
 * rather than drawing nothing -- see the fallback branch below. */
export type AvgMode = "single" | "double";

export interface ChartElements {
  bars: HTMLElement;
  xLabels: HTMLElement;
  yAxis: HTMLElement;
  tooltip: HTMLElement;
  infoHigh: HTMLElement;
  infoAvg: HTMLElement;
  infoLow: HTMLElement;
}

/** SW-9: 12-hour compact time ("2:32 PM", not "02:32 PM"), rendered in
 * `timeZone` (an IANA id from the chart's Timezone selector) or the
 * device-local zone when undefined. */
function formatBucketTime(b: Bucket, timeZone?: string): string {
  return new Date(b.endMs).toLocaleTimeString("en-US", {
    hour: "numeric",
    minute: "2-digit",
    hour12: true,
    timeZone,
  });
}

/** Tooltip-only (SW-1): compact date + the same time format the x-axis
 * uses -- "Aug 27 2:32 PM". X-axis labels stay time-only. */
function formatBucketDateTime(b: Bucket, timeZone?: string): string {
  const date = new Date(b.endMs).toLocaleDateString("en-US", { month: "short", day: "numeric", timeZone });
  return `${date} ${formatBucketTime(b, timeZone)}`;
}

export function renderChart(
  elements: ChartElements,
  buckets: Bucket[],
  avgMode: AvgMode = "single",
  timeZone?: string
): void {
  clear(elements.bars);
  clear(elements.xLabels);
  const CENTER = center(); // resolve the active scale's baseline once per render

  if (buckets.length === 0) {
    elements.bars.append(el("div", { class: "chart-empty" }, ["No chart data available."]));
    return;
  }

  const withData = buckets.filter((b) => b.hasData);
  // Values are mapped into plot space (Normalized signed level when that face
  // is active, raw ratio otherwise). CENTER is the baseline (0 for Normalized,
  // 1.0 for ratio). The transform is monotonic, so high stays >= low.
  // GEOMETRY pins at plotFloor() (Owner 2026-08-21): one -37 bar (or a feed
  // artifact) must not flatten the whole chart. Only pixels pin — the info
  // stats and the tooltip show the exact unpinned values.
  const FLOOR = plotFloor();
  const maxValue = withData.length > 0 ? Math.max(...withData.map((b) => toPlot(b.high as number))) : CENTER;
  const minValueExact = withData.length > 0 ? Math.min(...withData.map((b) => toPlot(b.low as number))) : CENTER;
  const minValue = Math.max(minValueExact, FLOOR);
  const range = Math.max(Math.abs(maxValue - CENTER), Math.abs(minValue - CENTER), 0.001);
  const scale = BAR_SCALE_PX / range;

  setText(elements.infoHigh, withData.length ? formatPlotValue(maxValue) : "--.--");
  setText(elements.infoLow, withData.length ? formatPlotValue(minValueExact) : "--.--");
  const avgOfAvgs =
    withData.length > 0 ? withData.reduce((sum, b) => sum + toPlot(b.avg as number), 0) / withData.length : null;
  setText(elements.infoAvg, avgOfAvgs !== null ? formatPlotValue(avgOfAvgs) : "--.--");

  renderYAxis(elements.yAxis, maxValue, minValue);

  const labelEvery = Math.max(1, Math.ceil(buckets.length / 6));

  buckets.forEach((bucket, index) => {
    const wrapper = el("div", {
      class: "bar-wrapper" + (bucket.isPartial ? " bar-wrapper-partial" : ""),
    });

    if (!bucket.hasData) {
      // Explicit, visually distinct "no data" marker -- never a fabricated
      // flat value at 1.0. See ops/dashboard-client-audit.md finding 8.
      wrapper.append(el("div", { class: "bar-gap" }));
    } else {
      const high = toPlot(bucket.high as number);
      const low = Math.max(toPlot(bucket.low as number), FLOOR);
      const avg = Math.max(toPlot(bucket.avg as number), FLOOR);

      if (high > CENTER) {
        const bar = el("div", { class: "bar bar-bullish" });
        bar.style.height = `${(high - CENTER) * scale}px`;
        wrapper.append(bar);
      }
      if (low < CENTER) {
        const bar = el("div", { class: "bar bar-bearish" });
        bar.style.height = `${(CENTER - low) * scale}px`;
        wrapper.append(bar);
      }
      if (avgMode === "single") {
        const marker = el("div", { class: "avg-marker" });
        marker.style.top = `calc(50% + ${(CENTER - avg) * scale}px)`;
        wrapper.append(marker);
      } else {
        // Genuine legacy-matching behavior (2026-07-22): btclock-ingest's
        // rollup tables now carry a real bullish/bearish split for every
        // bucket, computed at rollup time from real per-tick data (see
        // window.ts's RatioSpan.bullishAvg/bearishAvg and rollup.py) -- so
        // this no longer needs a single-marker fallback for rollup-sourced
        // buckets. A bucket showing null on one or both sides is a real
        // state (no tick on that side that period, or -- only for rows
        // that predate this column existing -- no split was ever computed
        // for it), not a gap to paper over.
        if (bucket.bullishAvg !== null) {
          const marker = el("div", { class: "avg-marker" });
          marker.style.top = `calc(50% + ${(CENTER - Math.max(toPlot(bucket.bullishAvg), FLOOR)) * scale}px)`;
          wrapper.append(marker);
        }
        if (bucket.bearishAvg !== null) {
          const marker = el("div", { class: "avg-marker" });
          marker.style.top = `calc(50% + ${(CENTER - Math.max(toPlot(bucket.bearishAvg), FLOOR)) * scale}px)`;
          wrapper.append(marker);
        }
      }
    }

    wrapper.addEventListener("mouseenter", () => showTooltip(elements.tooltip, wrapper, bucket, timeZone));
    wrapper.addEventListener("mouseleave", () => hideTooltip(elements.tooltip));

    elements.bars.append(wrapper);

    if (index % labelEvery === 0 || index === buckets.length - 1) {
      elements.xLabels.append(el("div", { class: "x-label" }, [formatBucketTime(bucket, timeZone)]));
    } else {
      elements.xLabels.append(el("div", { class: "x-label" }, [""]));
    }
  });
}

function renderYAxis(yAxis: HTMLElement, max: number, min: number): void {
  clear(yAxis);
  const CENTER = center();
  const range = Math.max(Math.abs(max - CENTER), Math.abs(min - CENTER), 0.001);
  const steps = [1, 0.75, 0.5, 0.25, 0, -0.25, -0.5, -0.75, -1];
  for (const step of steps) {
    yAxis.append(el("div", { class: "y-label" }, [formatPlotValue(CENTER + step * range)]));
  }
}

function showTooltip(tooltip: HTMLElement, anchor: HTMLElement, bucket: Bucket, timeZone?: string): void {
  clear(tooltip);
  const timeLabel = formatBucketDateTime(bucket, timeZone);
  const parts: HTMLElement[] = [
    el("div", { class: "tooltip-time" }, [
      timeLabel + (bucket.isPartial ? " (live, partial)" : ""),
    ]),
  ];

  if (!bucket.hasData) {
    parts.push(el("div", { class: "tooltip-row" }, ["No data in this period."]));
  } else {
    parts.push(row("HIGH", formatPlotValue(toPlot(bucket.high as number)), "high"));
    parts.push(row("AVG", formatPlotValue(toPlot(bucket.avg as number)), "avg"));
    parts.push(row("LOW", formatPlotValue(toPlot(bucket.low as number)), "low"));
  }

  tooltip.append(...parts);
  tooltip.classList.add("visible");
  const rect = anchor.getBoundingClientRect();
  tooltip.style.left = `${rect.left + rect.width / 2 - 70}px`;
  tooltip.style.top = `${rect.top - 100}px`;
}

function row(label: string, value: string, cls: string): HTMLElement {
  return el("div", { class: "tooltip-row" }, [
    el("span", { class: "tooltip-label" }, [label + ":"]),
    el("span", { class: `tooltip-value ${cls}` }, [value]),
  ]);
}

function hideTooltip(tooltip: HTMLElement): void {
  tooltip.classList.remove("visible");
}

function setText(node: HTMLElement, value: string): void {
  node.textContent = value;
}
